const CHANNEL_NAME = "abs-user-session";
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Same-tab bus: BroadcastChannel ignores the posting instance and used to drop sourceId === TAB_ID. */
export const SESSION_WINDOW_EVENT = "abs:session-user";

export type SessionBroadcastEventType =
  | "user:updated"
  | "avatar:updated"
  | "logout";

export interface SessionBroadcastEvent {
  type: SessionBroadcastEventType;
  userId?: number;
  codigo?: string;
  username?: string;
  timestamp: number;
  sourceId: string;
}

export function isSessionBroadcastAvailable() {
  return typeof BroadcastChannel !== "undefined";
}

function buildPayload(
  event: Omit<SessionBroadcastEvent, "timestamp" | "sourceId">,
): SessionBroadcastEvent {
  return {
    ...event,
    timestamp: Date.now(),
    sourceId: TAB_ID,
  };
}

function dispatchSameTab(payload: SessionBroadcastEvent) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SESSION_WINDOW_EVENT, { detail: payload }));
}

export function createSessionBroadcast(onEvent: (event: SessionBroadcastEvent) => void) {
  const onWindow = (e: Event) => {
    const detail = (e as CustomEvent<SessionBroadcastEvent>).detail;
    if (!detail?.type) return;
    onEvent(detail);
  };

  if (typeof window !== "undefined") {
    window.addEventListener(SESSION_WINDOW_EVENT, onWindow);
  }

  let channel: BroadcastChannel | null = null;
  if (isSessionBroadcastAvailable()) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (message) => {
        const event = message.data as SessionBroadcastEvent | undefined;
        // Same tab is handled via SESSION_WINDOW_EVENT.
        if (!event || event.sourceId === TAB_ID) return;
        onEvent(event);
      };
    } catch {
      channel = null;
    }
  }

  return {
    post(event: Omit<SessionBroadcastEvent, "timestamp" | "sourceId">) {
      postSessionBroadcast(event);
    },
    close() {
      if (typeof window !== "undefined") {
        window.removeEventListener(SESSION_WINDOW_EVENT, onWindow);
      }
      channel?.close();
    },
  };
}

export function postSessionBroadcast(event: Omit<SessionBroadcastEvent, "timestamp" | "sourceId">) {
  const payload = buildPayload(event);
  dispatchSameTab(payload);

  if (!isSessionBroadcastAvailable()) return;

  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(payload);
    channel.close();
  } catch {
    // BroadcastChannel is an optimization; same-tab window event still runs.
  }
}
